import { isEmptyObject } from "@entrilia/shared/utilities/objectHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type {
  Field,
  FieldIdToValueMap,
  LookupFieldConfiguration,
  ObjectTypeToLookupReferences,
  RelatedObjectFilter,
  RelatedObjectsResponse,
} from "../../api/types/objectTypes";
import { getLookupOptionAdditionalFieldValue } from "./helpers";
import type { RelatedObjectRowModel } from "./relatedObjectValuesState";

export type ValidationErrorsByObjectType = Record<string, ValidationErrorsByRow>;

// RowId -> FieldId -> ErrorMessage
export type ValidationErrorsByRow = Record<string, Record<string, string>>;

const isFieldValueEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

const isEmptyRequiredValue = (row: RelatedObjectRowModel, field: Field) =>
  isFieldValueEmpty(row[field.id]) && field.attributes.includes("Required");

const isInvalidLookupValue = (
  row: RelatedObjectRowModel,
  field: Field,
  validLookupValuesPerType: Record<string, Set<string>> | undefined
) => {
  const fieldValue = row[field.id];
  if (isFieldValueEmpty(fieldValue)) {
    return false;
  }

  if (!validLookupValuesPerType) {
    return false;
  }

  const lookupConfiguration =
    field.type === "Lookup" && field.configuration?.$type === "Lookup" ? field.configuration : undefined;

  if (!lookupConfiguration) {
    return false;
  }

  if (typeof fieldValue !== "string") {
    return true;
  }

  const validLookupValues = validLookupValuesPerType[lookupConfiguration.objectType];
  return validLookupValues ? !validLookupValues.has(fieldValue) : !field.attributes.includes("HiddenByDefault");
};

const getValidationErrors = (
  rows: RelatedObjectRowModel[],
  fields: Field[],
  lookupOptions: ObjectTypeToLookupReferences | undefined
): Record<string, Record<string, string>> => {
  const validLookupValuesPerType = lookupOptions
    ? Object.entries(lookupOptions).reduce(
        (result, entry) => {
          const [objectType, references] = entry;
          return { ...result, [objectType]: new Set(references.map((ref) => ref.objectId)) };
        },
        {} as Record<string, Set<string>>
      )
    : undefined;

  return rows.reduce(
    (result, row) => {
      result[row.id] = {};

      for (const field of fields) {
        if (isEmptyRequiredValue(row, field)) {
          defined(result[row.id])[field.id] = "Required field";
        } else if (isInvalidLookupValue(row, field, validLookupValuesPerType)) {
          defined(result[row.id])[field.id] = "Invalid lookup value";
        }
      }

      return result;
    },
    {} as Record<string, Record<string, string>>
  );
};

export const getValidationErrorsByObjectType = (
  rowsByObjectType: Record<string, RelatedObjectRowModel[]>,
  fieldsByObjectType: Record<string, Field[]>,
  lookupOptionsByObjectType: Record<string, ObjectTypeToLookupReferences> | undefined
): ValidationErrorsByObjectType => {
  const result: ValidationErrorsByObjectType = {};

  for (const [objectType, rows] of Object.entries(rowsByObjectType)) {
    const fields = fieldsByObjectType[objectType] ?? [];
    const lookupOptions = lookupOptionsByObjectType ? lookupOptionsByObjectType[objectType] : undefined;
    const errors = getValidationErrors(rows, fields, lookupOptions);
    result[objectType] = errors;
  }

  return result;
};

export const areRelatedObjectRowsValid = (validationErrorsByObjectType: ValidationErrorsByObjectType): boolean =>
  Object.values(validationErrorsByObjectType).every((errorsByRow) =>
    Object.values(errorsByRow).every((errors) => isEmptyObject(errors))
  );

interface LookupFilterValue {
  filterConfig: RelatedObjectFilter;
  removeAll?: boolean;
  fieldValue?: string;
}

const getLookupFilterValuesForRelatedObjects = (
  lookupFieldsWithFilters: Field[],
  fieldValues: FieldIdToValueMap,
  lookupOptions: ObjectTypeToLookupReferences
): LookupFilterValue[] => {
  const result: LookupFilterValue[] = [];

  for (const lookupField of lookupFieldsWithFilters) {
    const lookupConfig = lookupField.configuration as LookupFieldConfiguration;
    for (const filterConfig of lookupConfig.relatedObjectFilters) {
      const targetOptions = lookupOptions[lookupConfig.objectType];
      if (!targetOptions) {
        continue;
      }

      const fieldValueAsObjectId = fieldValues[lookupField.id];
      if (typeof fieldValueAsObjectId !== "string" || !fieldValueAsObjectId) {
        result.push({ filterConfig, removeAll: true }); // Remove all if no valid lookup value is selected
        continue;
      }

      const filterFieldValue = getLookupOptionAdditionalFieldValue(
        fieldValueAsObjectId,
        targetOptions,
        filterConfig.lookupObjectComparisonFieldId
      );

      result.push({ filterConfig, fieldValue: filterFieldValue });
    }
  }

  return result;
};

const filterLookupOptionsOfRelatedObjects = (
  objectType: string,
  filterValues: LookupFilterValue[],
  lookupOptions: ObjectTypeToLookupReferences
): ObjectTypeToLookupReferences => {
  const relatedFilterValues = filterValues.filter((f) => f.filterConfig.relatedObjectType === objectType);
  if (relatedFilterValues.length === 0) {
    return lookupOptions;
  }

  const result = Object.fromEntries(
    Object.entries(lookupOptions).map(([objectType, references]) => [objectType, [...references]])
  );

  for (const filterValue of relatedFilterValues) {
    const { comparisonObjectType, comparisonObjectFieldId } = filterValue.filterConfig;
    if (!comparisonObjectType || !comparisonObjectFieldId) {
      continue; // This case is not supported yet
    }

    if (filterValue.removeAll) {
      result[comparisonObjectType] = [];
    } else {
      const filteredOptions = (result[comparisonObjectType] ?? []).filter((option) =>
        option.additionalFieldValues?.some(
          (additionalField) =>
            additionalField.fieldId === comparisonObjectFieldId && additionalField.fieldValue === filterValue.fieldValue
        )
      );

      result[comparisonObjectType] = filteredOptions;
    }
  }

  return result;
};

export const getValidLookupOptionsOfRelatedObjects = (
  fieldValues: FieldIdToValueMap,
  fields: Field[],
  lookupOptions: ObjectTypeToLookupReferences,
  relatedObjectResponses: RelatedObjectsResponse[]
) => {
  const result: Record<string, ObjectTypeToLookupReferences> = {};

  const lookupFieldsWithFilters = fields.filter(
    (field) => field.configuration?.$type === "Lookup" && field.configuration.relatedObjectFilters.length > 0
  );

  const lookupFilterValues = getLookupFilterValuesForRelatedObjects(
    lookupFieldsWithFilters,
    fieldValues,
    lookupOptions
  );

  for (const relatedObjectsData of relatedObjectResponses) {
    const relatedObjectType = relatedObjectsData.objectType;

    if (!relatedObjectsData.lookupOptions) {
      result[relatedObjectType] = {};
      continue;
    }

    result[relatedObjectType] = filterLookupOptionsOfRelatedObjects(
      relatedObjectType,
      lookupFilterValues,
      relatedObjectsData.lookupOptions
    );
  }

  return result;
};
